import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
//
import { customBaseQuery } from '../../auth/context/jwt/utils';
import axios from '../../utils/axios';
import {
  IConvertQuotation,
  IQuotation,
  IQuotationCreate,
  IQuotationPage,
  IQuotationSummary,
} from '../../types/quotation';
import { IContract } from '../../types/contract';

const path = 'quotations';

type UpdateProps = {
  data: IQuotationCreate;
  id: number;
};

export const quotationService = createApi({
  reducerPath: 'quotationService',
  tagTypes: ['quotations', 'contracts'],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getQuotations: builder.query<IQuotationPage, Record<string, unknown>>({
      query: (params) => ({
        url: `/${path}/list`,
        params,
      }),
      providesTags: ['quotations'],
    }),

    getQuotationSummary: builder.query<IQuotationSummary, number>({
      query: (clientId) => ({ url: `/${path}/summary`, params: { clientId } }),
      providesTags: ['quotations'],
    }),

    getQuotation: builder.query<IQuotation, number>({
      query: (id: number) => ({
        url: `/${path}/findById/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'quotations', id }],
    }),

    saveQuotation: builder.mutation<IQuotation, IQuotationCreate>({
      query: (body) => ({
        url: `/${path}/insert`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['quotations'],
    }),

    updateQuotation: builder.mutation<IQuotation, UpdateProps>({
      query: ({ id, data }) => ({
        url: `/${path}/update/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['quotations', { type: 'quotations', id }],
    }),

    deleteQuotation: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/${path}/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['quotations'],
    }),

    sendQuotation: builder.mutation<IQuotation, number>({
      query: (id) => ({ url: `/${path}/${id}/send`, method: 'POST' }),
      invalidatesTags: ['quotations'],
    }),

    viewQuotation: builder.mutation<IQuotation, number>({
      query: (id) => ({ url: `/${path}/${id}/view`, method: 'POST' }),
      invalidatesTags: ['quotations'],
    }),

    negotiateQuotation: builder.mutation<IQuotation, number>({
      query: (id) => ({ url: `/${path}/${id}/negotiate`, method: 'POST' }),
      invalidatesTags: ['quotations'],
    }),

    approveQuotation: builder.mutation<IQuotation, number>({
      query: (id) => ({ url: `/${path}/${id}/approve`, method: 'POST' }),
      invalidatesTags: ['quotations'],
    }),

    rejectQuotation: builder.mutation<IQuotation, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/${path}/${id}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['quotations'],
    }),

    cancelQuotation: builder.mutation<IQuotation, { id: number; reason: string }>({
      query: ({ id, reason }) => ({ url: `/${path}/${id}/cancel`, method: 'POST', body: { reason } }),
      invalidatesTags: ['quotations'],
    }),

    sendQuotationEmail: builder.mutation<
      IQuotation,
      { id: number; data: { to: string; cc?: string; message?: string } }
    >({
      query: ({ id, data }) => ({ url: `/${path}/${id}/send-email`, method: 'POST', body: data }),
      invalidatesTags: ['quotations'],
    }),

    duplicateQuotation: builder.mutation<IQuotation, number>({
      query: (id) => ({ url: `/${path}/${id}/duplicate`, method: 'POST' }),
      invalidatesTags: ['quotations'],
    }),

    convertQuotation: builder.mutation<IContract, { id: number; data: IConvertQuotation }>({
      query: ({ id, data }) => ({
        url: `/${path}/${id}/convert`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['quotations', 'contracts'],
    }),
  }),
});

export const downloadQuotationPdf = async (id: number, fallbackName: string) => {
  const response = await axios.get(`/${path}/${id}/pdf`, { responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  const fileName = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '').split(';')[0] || fallbackName
    : fallbackName;
  saveAs(new Blob([response.data], { type: 'application/pdf' }), fileName);
};

export const {
  useGetQuotationsQuery,
  useGetQuotationQuery,
  useGetQuotationSummaryQuery,
  useSaveQuotationMutation,
  useUpdateQuotationMutation,
  useDeleteQuotationMutation,
  useSendQuotationMutation,
  useViewQuotationMutation,
  useNegotiateQuotationMutation,
  useApproveQuotationMutation,
  useRejectQuotationMutation,
  useCancelQuotationMutation,
  useConvertQuotationMutation,
  useDuplicateQuotationMutation,
  useSendQuotationEmailMutation,
} = quotationService;
